import {
  Input,
  Typography,
  Textarea,
  Button,
  showToast,
} from '@deeptrue-frontend/ui';
import styled from 'styled-components/macro';
import { Field, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { useFormatMessage } from 'components/IntlProvider';
import Grid from '@material-ui/core/Grid';
import api from 'api';
import { translateFieldErrors } from 'components/IntlProvider/translateFieldErrors';
import { LinkType } from '../LinksList/Link';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import * as Yup from 'yup';

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape({
    title: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    sections: Yup.array().required(
      t('errors.fields.required', 'Field is required')
    ),
  });

const StyledLabel = styled.label<{
  checked: boolean;
  error: boolean;
  isDisabled?: boolean;
}>`
  border: ${({ checked }) =>
    checked ? '1px solid #1656E9' : '1px solid #c7cad1'};

  padding: 0px 1.125rem;
  background-color: ${({ checked }) => (checked ? '#E8EFFC' : '#FFFFFF')};
  input {
    display: none;
  }
  cursor: pointer;
  border: ${({ error }) => (error ? '1px solid #DC3545' : '')};
  display: flex;
  align-items: center;
  min-height: 3rem;
  border-radius: 8px;
  opacity: ${({ isDisabled }) => (isDisabled ? '0.5' : '1')};
`;

const FakeRadio = styled.div<{ checked: boolean }>`
  border-radius: 50%;
  width: 20px;
  min-width: 20px;
  height: 20px;
  border: ${({ checked }) =>
    checked ? '5px solid #1656E9' : '1px solid #c7cad1'};
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  margin-right: 1rem;
`;

const Section = styled.section`
  margin: 1rem 0;
  padding: 0 1.5rem;
`;

const SectionTitle = styled(Typography)`
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const PasswordWrapper = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  margin-top: 1rem;

  label {
    margin: 0;
  }

  input {
    opacity: ${({ isDisabled }) => (isDisabled ? '0.5' : '1')};
  }
`;

const StyledInput = styled(Input)`
  margin: 0;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #dedede;
  margin: 0 0 1rem 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  input {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 1rem;
    accent-color: #1656e9;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

const sections = ['all', 'results', 'specification', 'reportRecommendations'];

const Error = styled.span`
  color: #dc3545 !important;
`;

export type InitialValues = {
  access_type: 'public' | 'password_protected';
  default_language: 'en' | 'fr' | 'de' | 'it';
  title: string;
  description: string;
  sections: string[];
  id?: number;
  password?: string;
  is_disabled: boolean;
};

interface Props {
  setIsFormOpen: (val: boolean) => void;
  organization: string;
  surveyId: string;
  initialValues?: InitialValues;
  setInitialValues: (values: InitialValues | undefined) => void;
  addLink: (link: LinkType) => void;
  updateLink: (link: LinkType) => void;
}

export const GenerateLinkForm = ({
  setIsFormOpen,
  organization,
  surveyId,
  initialValues,
  setInitialValues,
  addLink,
  updateLink,
}: Props) => {
  const t = useFormatMessage();
  const isEditing = !!(initialValues && initialValues.id);

  const submitValues = (values: any, { setErrors }: FormikHelpers<any>) => {
    const isPasswordProtected = values.access_type === 'password_protected';
    if (isEditing) {
      return api.resources
        .editSharingLink({
          organizationCodename: organization,
          linkId: initialValues?.id as number,
          surveyId: surveyId,
          data: {
            access_type: values.access_type,
            sections: values.sections.filter(
              (section: string) => section !== 'all'
            ),
            default_language: values.default_language,
            title: values.title,
            description: values.description,
            ...(isPasswordProtected ? { password: values.password } : {}),
            is_disabled: values.is_disabled,
          },
        })
        .then((resp) => {
          updateLink(resp.data);
          showToast({
            type: 'success',
            title: t(
              'project.share.shareReportLinks.linkEdited',
              'Link edited'
            ),
          });
          setIsFormOpen(false);
        })
        .catch((err) => {
          setErrors(
            translateFieldErrors({
              t,
              err: err.response.data,
            })
          );
        });
    }
    return api.resources
      .createSharingLink({
        organizationCodename: organization,
        surveyId: surveyId,
        data: {
          access_type: values.access_type,
          sections: values.sections.filter(
            (section: string) => section !== 'all'
          ),
          default_language: values.default_language,
          title: values.title,
          description: values.description,
          ...(isPasswordProtected ? { password: values.password } : {}),
          is_disabled: values.is_disabled,
        },
      })
      .then((resp) => {
        addLink(resp.data);
        showToast({ type: 'success', title: 'Link generated' });
        setIsFormOpen(false);
      })
      .catch((err) => {
        setErrors(
          translateFieldErrors({
            t,
            err: err.response.data,
          })
        );
      });
  };

  const defaultValues = {
    access_type: 'public',
    sections: sections,
    default_language: 'en',
    is_disabled: false,
    title: '',
  };

  return (
    <div>
      <Typography padding="0 1.5rem">
        {t(
          'project.share.shareReportLinks.descriptionForm',
          'Share this report with others by sending them the generated link.'
        )}
      </Typography>
      <Formik
        initialValues={initialValues || defaultValues}
        onSubmit={submitValues}
        validationSchema={getValidationSchema(t)}
      >
        {({
          handleSubmit,
          isSubmitting,
          errors,
          values,
          setFieldValue,
          touched,
        }) => {
          const isPasswordProtected =
            values.access_type === 'password_protected';
          return (
            <div>
              <Section>
                <SectionTitle>
                  {t(
                    'project.share.shareReportLinks.accessType',
                    'Access type'
                  )}
                </SectionTitle>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.access_type === 'public'}
                      error={!!errors.access_type}
                      isDisabled={isEditing}
                    >
                      <FakeRadio checked={values.access_type === 'public'} />
                      <Field
                        type="radio"
                        name="access_type"
                        value="public"
                        disabled={isEditing}
                      />
                      {t(
                        'project.share.shareReportLinks.accessPublic',
                        'Public'
                      )}
                    </StyledLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.access_type === 'password_protected'}
                      error={!!errors.access_type}
                      isDisabled={isEditing}
                    >
                      <FakeRadio
                        checked={values.access_type === 'password_protected'}
                      />
                      <Field
                        type="radio"
                        name="access_type"
                        value="password_protected"
                        disabled={isEditing}
                      />
                      {t(
                        'project.share.shareReportLinks.accessPrivate',
                        'Password restricted'
                      )}
                    </StyledLabel>
                  </Grid>
                </Grid>
                {isPasswordProtected && (
                  <Grid item xs={12}>
                    <PasswordWrapper isDisabled={isEditing}>
                      <Typography mr={'1rem'}>
                        {t('common.password', 'Password')}
                      </Typography>
                      <Field
                        as={StyledInput}
                        name="password"
                        placeholder={t(
                          'project.share.shareReportLinks.typePassword',
                          'Type password'
                        )}
                        disabled={isEditing}
                      />
                    </PasswordWrapper>
                  </Grid>
                )}
              </Section>

              <Separator />

              <Section>
                <SectionTitle>
                  {t('project.share.shareReportLinks.title', 'Title')}
                </SectionTitle>
                <Grid item xs={12}>
                  <Field
                    as={StyledInput}
                    name="title"
                    hasError={touched.title && errors.title}
                    helperText={touched.title && errors.title}
                    placeholder={t(
                      'project.share.shareReportLinks.typeTitle',
                      'Type a title'
                    )}
                  />
                </Grid>
              </Section>

              <Separator />

              <Section>
                <SectionTitle>
                  {t(
                    'project.share.shareReportLinks.description',
                    'Description (optional)'
                  )}
                </SectionTitle>
                <Grid item xs={12}>
                  <Field
                    as={Textarea}
                    name="description"
                    placeholder={t(
                      'project.share.shareReportLinks.typeDescription',
                      'Type description'
                    )}
                  />
                </Grid>
              </Section>

              <Separator />
              <Section>
                <SectionTitle>
                  {t(
                    'project.share.shareReportLinks.sectionsVisibility',
                    'Report sections visibility'
                  )}
                </SectionTitle>

                <Grid container>
                  <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field
                        type="checkbox"
                        name="sections"
                        label="all"
                        value="all"
                        checked={
                          values.sections.filter(
                            (section: string) => section !== 'all'
                          ).length ===
                          sections.length - 1
                        }
                        onChange={() => {
                          // @ts-ignore
                          if (values.sections.includes('all')) {
                            return setFieldValue('sections', []);
                          }
                          return setFieldValue('sections', sections);
                        }}
                      />
                      <Typography>
                        {t('project.share.shareReportLinks.section.all', 'All')}
                      </Typography>
                    </CheckboxLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field
                        type="checkbox"
                        name="sections"
                        label="Results"
                        value="results"
                      />
                      <Typography>
                        {t(
                          'project.share.shareReportLinks.section.results',
                          'Results'
                        )}
                      </Typography>
                    </CheckboxLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field
                        type="checkbox"
                        name="sections"
                        value="specification"
                      />
                      <Typography>
                        {t(
                          'project.share.shareReportLinks.section.specification',
                          'Specification'
                        )}
                      </Typography>
                    </CheckboxLabel>
                  </Grid>

                  <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field
                        type="checkbox"
                        name="sections"
                        value="reportRecommendations"
                      />
                      <Typography>
                        {t(
                          'project.share.shareReportLinks.section.insight',
                          'Insights summary'
                        )}
                      </Typography>
                    </CheckboxLabel>
                  </Grid>
                  {/* <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field type="checkbox" name="sections" value="files" />
                      <Typography>
                        {t(
                          'project.share.shareReportLinks.section.files',
                          'Files'
                        )}
                      </Typography>
                    </CheckboxLabel>
                  </Grid> */}
                  {/* <Grid item xs={6}>
                    <CheckboxLabel>
                      <Field
                        type="checkbox"
                        name="sections"
                        value="filtering"
                      />
                      <Typography>Results filtering</Typography>
                    </CheckboxLabel>
                  </Grid> */}
                </Grid>

                {touched.sections && errors.sections && (
                  <Error>
                    {t('errors.fields.required', 'Field is required')}
                  </Error>
                )}
              </Section>

              <Separator />
              <Section>
                <SectionTitle>
                  {t(
                    'project.share.shareReportLinks.defaultLang',
                    'Default language'
                  )}
                </SectionTitle>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.default_language === 'en'}
                      error={!!errors.default_language}
                    >
                      <FakeRadio checked={values.default_language === 'en'} />
                      <Field type="radio" name="default_language" value="en" />
                      {t('english', 'English')}
                    </StyledLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.default_language === 'de'}
                      error={!!errors.default_language}
                    >
                      <FakeRadio checked={values.default_language === 'de'} />
                      <Field type="radio" name="default_language" value="de" />
                      {t('german', 'German')}
                    </StyledLabel>
                  </Grid>

                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.default_language === 'fr'}
                      error={!!errors.default_language}
                    >
                      <FakeRadio checked={values.default_language === 'fr'} />
                      <Field type="radio" name="default_language" value="fr" />
                      {t('french', 'French')}
                    </StyledLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <StyledLabel
                      checked={values.default_language === 'it'}
                      error={!!errors.default_language}
                    >
                      <FakeRadio checked={values.default_language === 'it'} />
                      <Field type="radio" name="default_language" value="it" />
                      {t('italian', 'Italian')}
                    </StyledLabel>
                  </Grid>
                </Grid>
              </Section>
              <Separator />
              <Section>
                <SectionTitle>
                  {t('project.share.shareReportLinks.isActive', 'Status')}
                </SectionTitle>
                <Grid item xs={12}>
                  <CheckboxLabel>
                    <Field type="checkbox" name="is_disabled" />
                    <Typography>
                      {t(
                        'project.share.shareReportLinks.section.disabled',
                        'Disabled'
                      )}
                    </Typography>
                  </CheckboxLabel>
                </Grid>
              </Section>
              <Separator />
              <Section>
                <ButtonWrapper>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => {
                      setIsFormOpen(false);
                      setInitialValues(undefined);
                    }}
                  >
                    {t('project.share.shareReportLinks.cancel', 'Cancel')}
                  </Button>

                  <Button
                    // @ts-ignore
                    onClick={handleSubmit}
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    {isEditing
                      ? t(
                          'project.share.shareReportLinks.updateLink',
                          'Update link'
                        )
                      : t(
                          'project.share.shareReportLinks.generateSharingLink',
                          'Generate sharing link'
                        )}
                  </Button>
                </ButtonWrapper>
              </Section>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};
